import { FunctionComponent, useCallback, useState } from 'react';
import { Button, RadioGroup, RadioGroupItemLabel, RadioGroupShell } from '@schuettflix/planum-react';
import { MessageBox, TextField } from '@schuettflix/react-components';
import { AACHEN_PRESET, type PositionTemplate, PositionTemplatesArraySchema, type Preset } from './presets.ts';
import { DeleteIcon, PlusIcon } from '@schuettflix/icons-react';
import { bulkPositionConfig } from './config/index.ts';
import {
    ConstructionProjectSearchDropdown,
    type SelectedConstructionProject,
} from './ConstructionProjectSearchDropdown.tsx';
import { useCreatePositionClientV2 } from '@/clients/position/positionV2.ts';
import type { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import type { PostPositionConfigurationRequest } from '@/shared/types/positionV2/api/post-position-configuration.ts';

interface Target {
    id: string;
    constructionProject: SelectedConstructionProject | null;
    manualCostCenter: string;
}

interface CreationResult {
    templateName: string;
    projectNumber: string;
    success: boolean;
    positionId?: string;
    positionNumber?: string;
    error?: string;
}

function generateId(): string {
    return Math.random().toString(36).substring(2, 9);
}

function createEmptyTarget(): Target {
    return {
        id: generateId(),
        constructionProject: null,
        manualCostCenter: '',
    };
}

type SelectedTemplate = 'Aachen';

const templatePresetMap = new Map<SelectedTemplate, Preset>([['Aachen', AACHEN_PRESET]]);

export function BulkPositionCreatorPage() {
    const { createPositionV2 } = useCreatePositionClientV2();

    const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate | undefined>(undefined);
    const [positionTemplatesJson, setPositionTemplatesJson] = useState<string>('');
    const [partnerOrganizationId, setPartnerOrganizationId] = useState<string>('');

    const [validationErrors, setValidationErrors] = useState<{
        templates: string | null;
        partnerOrgId: string | null;
        targets: string | null;
    }>({ templates: null, partnerOrgId: null, targets: null });

    const [targets, setTargets] = useState<Target[]>([createEmptyTarget()]);

    const [isCreating, setIsCreating] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [results, setResults] = useState<CreationResult[]>([]);

    const applyTemplate = useCallback((template: SelectedTemplate) => {
        const preset = templatePresetMap.get(template);
        if (!preset) {
            throw new Error('Missing mapping from template to preset');
        }
        setSelectedTemplate(template);
        setPositionTemplatesJson(JSON.stringify(preset.positionTemplates, null, 2));
        setPartnerOrganizationId(preset.partnerOrganizationId?.toString() ?? '');
        setValidationErrors({ templates: null, partnerOrgId: null, targets: null });
    }, []);

    const addTarget = useCallback(() => {
        setTargets(prev => [...prev, createEmptyTarget()]);
    }, []);

    const removeTarget = useCallback((id: string) => {
        setTargets(prev => (prev.length > 1 ? prev.filter(t => t.id !== id) : prev));
    }, []);

    const updateTargetConstructionProject = useCallback(
        (id: string, constructionProject: SelectedConstructionProject | null) => {
            setTargets(prev => prev.map(t => (t.id === id ? { ...t, constructionProject } : t)));
            setValidationErrors(prev => ({ ...prev, targets: null }));
        },
        []
    );

    const updateTargetCostCenter = useCallback((id: string, manualCostCenter: string) => {
        setTargets(prev => prev.map(t => (t.id === id ? { ...t, manualCostCenter } : t)));
    }, []);

    const validateAll = useCallback((): { templates: PositionTemplate[]; validTargets: Target[] } | null => {
        const errors = {
            templates: null as string | null,
            partnerOrgId: null as string | null,
            targets: null as string | null,
        };
        let hasErrors = false;

        // Validate templates JSON and schema
        let templates: PositionTemplate[] = [];
        try {
            const parsed = JSON.parse(positionTemplatesJson);
            const result = PositionTemplatesArraySchema.safeParse(parsed);
            if (!result.success) {
                const firstError = result.error.errors[0];
                errors.templates = `Invalid template: ${firstError.path.join('.')} - ${firstError.message}`;
                hasErrors = true;
            } else {
                templates = result.data;
            }
        } catch (e) {
            errors.templates = `Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`;
            hasErrors = true;
        }

        // Validate partner organization ID
        if (!partnerOrganizationId || partnerOrganizationId.trim() === '') {
            errors.partnerOrgId = 'Partner Organization ID is required';
            hasErrors = true;
        } else if (isNaN(parseInt(partnerOrganizationId, 10))) {
            errors.partnerOrgId = 'Partner Organization ID must be a number';
            hasErrors = true;
        }

        // Validate targets - all must have construction project selected
        const validTargets = targets.filter(t => t.constructionProject !== null);
        const invalidTargets = targets.filter(t => t.constructionProject === null);
        if (invalidTargets.length > 0) {
            errors.targets = `${invalidTargets.length} target(s) missing construction project selection`;
            hasErrors = true;
        }
        if (validTargets.length === 0) {
            errors.targets = 'At least one target with a construction project is required';
            hasErrors = true;
        }

        setValidationErrors(errors);

        if (hasErrors) {
            return null;
        }

        return { templates, validTargets };
    }, [positionTemplatesJson, partnerOrganizationId, targets]);

    const buildPayload = useCallback(
        (template: PositionTemplate, target: Target) => {
            const cp = target.constructionProject;
            if (!cp) throw new Error('Construction project is required');

            const partnerOrgId = partnerOrganizationId ? parseInt(partnerOrganizationId, 10) : null;

            const productConfigurations = template.productConfigurations.map(pc => ({
                ...pc,
                partnerOrganizationId: partnerOrgId,
            }));

            const financeSettings: FinanceSettings[] = bulkPositionConfig.financeSettingsTemplate.map(fs => {
                const base = {
                    debtorOrganizationId:
                        fs.debtorOrganizationId === '$TARGET_ORG' ? cp.organizationId : fs.debtorOrganizationId,
                    creditorOrganizationId:
                        fs.creditorOrganizationId === '$TARGET_ORG' ? cp.organizationId : fs.creditorOrganizationId,
                    billingText: null,
                };

                switch (fs.billingMethod) {
                    case 'CREDIT_NOTE':
                    case 'INVOICE':
                        return {
                            ...base,
                            billingMethod: fs.billingMethod,
                            paymentTermId: fs.paymentTermId,
                        };
                    case 'PARTNER_INVOICE':
                        return {
                            ...base,
                            billingMethod: fs.billingMethod,
                            paymentTermId: fs.paymentTermId,
                        };
                }
            });

            return {
                projectNumber: cp.projectCode,
                customer: {
                    constructionProjectId: cp.id,
                    organizationId: cp.organizationId,
                    userId: cp.userId,
                    manualCostCenter: target.manualCostCenter || null,
                },
                businessCase: bulkPositionConfig.businessCase,
                platformOrganizationId: bulkPositionConfig.platformOrganizationId,
                marketCode: bulkPositionConfig.marketCode,
                validFrom: new Date(bulkPositionConfig.validFrom),
                validTo: new Date(bulkPositionConfig.validTo),
                active: bulkPositionConfig.active,
                note: bulkPositionConfig.note,
                financeSettings,
                productConfigurations,
            } as PostPositionConfigurationRequest;
        },
        [partnerOrganizationId]
    );

    const handleCreate = useCallback(async () => {
        const validation = validateAll();
        if (!validation) {
            return;
        }

        const { templates, validTargets } = validation;

        const workItems: { template: PositionTemplate; target: Target }[] = [];
        for (const target of validTargets) {
            for (const template of templates) {
                workItems.push({ template, target });
            }
        }

        setIsCreating(true);
        setProgress({ current: 0, total: workItems.length });
        setResults([]);

        const newResults: CreationResult[] = [];

        for (let i = 0; i < workItems.length; i++) {
            const { template, target } = workItems[i];
            const projectCode = target.constructionProject?.projectCode ?? 'unknown';
            setProgress({ current: i + 1, total: workItems.length });

            try {
                const payload = buildPayload(template, target);
                const response = await createPositionV2(payload);

                newResults.push({
                    templateName: template.name,
                    projectNumber: projectCode,
                    success: true,
                    positionId: response.id,
                    positionNumber: response.projectPositionNumber,
                });
            } catch (error) {
                let errorMessage = 'Unknown error';
                if (error && typeof error === 'object' && 'response' in error) {
                    const axiosError = error as { response?: { data?: { message?: string } } };
                    errorMessage =
                        axiosError.response?.data?.message || JSON.stringify(axiosError.response?.data) || errorMessage;
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                newResults.push({
                    templateName: template.name,
                    projectNumber: projectCode,
                    success: false,
                    error: errorMessage,
                });
            }

            setResults([...newResults]);
        }

        setIsCreating(false);
    }, [validateAll, buildPayload, createPositionV2]);

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    const RadioGroupItem = useCallback<FunctionComponent<{ template: SelectedTemplate }>>(
        ({ template }) => (
            <RadioGroupShell
                value={template}
                variant="card"
                disabled={!templatePresetMap.get(template) || selectedTemplate === template}
                onClick={() => applyTemplate(template)}
            >
                <RadioGroupItemLabel>{template}</RadioGroupItemLabel>
            </RadioGroupShell>
        ),
        [selectedTemplate, applyTemplate]
    );

    return (
        <div className="mx-auto max-w-4xl p-6">
            <h1 className="mb-2 text-2xl font-bold">Bulk Position Creator</h1>
            <p className="mb-6 text-sm text-gray-600">
                Create multiple positions for one or more construction projects at once. The position templates below
                are pre-configured and don't need to be edited. To create positions: select the hub's product catalog,
                enter the Partner Organization ID, select the construction project(s) you want to add positions to, and
                click "Create Positions".
            </p>

            {/* Preset Buttons */}
            <label className="mb-1 block text-sm font-medium">Available Hub Presets</label>
            <div className="mb-4 flex gap-2">
                <RadioGroup defaultValue={selectedTemplate} className="grid-flow-col">
                    <RadioGroupItem template="Aachen" />
                </RadioGroup>
            </div>

            {/* Position Templates Textarea */}
            <div className="mb-4">
                {validationErrors.templates && (
                    <MessageBox type="error" className="mt-2">
                        {validationErrors.templates}
                    </MessageBox>
                )}
            </div>

            {selectedTemplate !== undefined && (
                <>
                    {/* Partner Organization ID */}
                    <div className="mb-6">
                        <label className="mb-1 block text-sm font-medium">
                            Partner Organization ID (applied to all positions)
                        </label>
                        <TextField
                            value={partnerOrganizationId}
                            onChange={e => {
                                setPartnerOrganizationId(e.target.value);
                                setValidationErrors(prev => ({ ...prev, partnerOrgId: null }));
                            }}
                            placeholder="e.g. 9997"
                            className="max-w-xs"
                            errorMessage={validationErrors.partnerOrgId ?? undefined}
                        />
                    </div>

                    {/* Targets */}
                    <div className="mb-6">
                        <div className="mb-2 flex items-center justify-between">
                            <label className="text-sm font-medium">Targets</label>
                            <Button variant="secondary" size="sm" onClick={addTarget}>
                                <PlusIcon className="mr-1 h-4 w-4" />
                                Add Target
                            </Button>
                        </div>
                        {validationErrors.targets && (
                            <MessageBox type="error" className="mb-2">
                                {validationErrors.targets}
                            </MessageBox>
                        )}

                        <div className="space-y-3">
                            {targets.map((target, index) => (
                                <div key={target.id} className="bg-surface-subdued rounded border p-3">
                                    <div className="mb-2 flex items-center justify-between">
                                        <span className="text-xs font-medium">Target {index + 1}</span>
                                        {targets.length > 1 && (
                                            <button
                                                onClick={() => removeTarget(target.id)}
                                                className="text-critical hover:text-critical-dark"
                                            >
                                                <DeleteIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        <div>
                                            <label className="mb-1 block text-xs text-gray-600">
                                                Construction Project
                                            </label>
                                            <ConstructionProjectSearchDropdown
                                                value={target.constructionProject}
                                                onChange={cp => updateTargetConstructionProject(target.id, cp)}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-xs text-gray-600">
                                                Cost Center (optional)
                                            </label>
                                            <TextField
                                                value={target.manualCostCenter}
                                                onChange={e => updateTargetCostCenter(target.id, e.target.value)}
                                                placeholder="Cost Center"
                                            />
                                        </div>
                                    </div>
                                    {target.constructionProject && (
                                        <div className="mt-2 text-xs text-gray-500">
                                            <span className="mr-4">
                                                Project: <strong>{target.constructionProject.projectCode}</strong>
                                            </span>
                                            <span className="mr-4">
                                                Org: <strong>{target.constructionProject.organizationId}</strong> (
                                                {target.constructionProject.organizationName})
                                            </span>
                                            <span>
                                                User: <strong>{target.constructionProject.userId}</strong>
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Create Button */}
                    <div className="mb-6">
                        <Button variant="primary" onClick={handleCreate} loading={isCreating} disabled={isCreating}>
                            {isCreating ? `Creating... (${progress.current}/${progress.total})` : 'Create Positions'}
                        </Button>
                    </div>
                </>
            )}

            {/* Progress & Results */}
            {(isCreating || results.length > 0) && (
                <div className="rounded border p-4">
                    {/* Progress Bar */}
                    {isCreating && (
                        <div className="mb-4">
                            <div className="mb-1 flex justify-between text-sm">
                                <span>Progress</span>
                                <span>
                                    {progress.current} / {progress.total}
                                </span>
                            </div>
                            <div className="bg-surface-subdued h-2 overflow-hidden rounded-full">
                                <div
                                    className="bg-brand-primary h-full transition-all"
                                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Summary */}
                    {results.length > 0 && (
                        <div className="mb-4 flex gap-4">
                            <span className="text-sm">
                                Total: <strong>{results.length}</strong>
                            </span>
                            <span className="text-success text-sm">
                                Success: <strong>{successCount}</strong>
                            </span>
                            <span className="text-critical text-sm">
                                Failed: <strong>{failureCount}</strong>
                            </span>
                        </div>
                    )}

                    {/* Results List */}
                    {results.length > 0 && (
                        <div className="max-h-64 space-y-1 overflow-y-auto text-xs">
                            {results.map((result, index) => (
                                <div
                                    key={index}
                                    className={`rounded p-2 ${result.success ? 'bg-success-light' : 'bg-critical-light'}`}
                                >
                                    <span className="font-medium">{result.templateName}</span>
                                    <span className="mx-1">-&gt;</span>
                                    <span>{result.projectNumber}</span>
                                    {result.success ? (
                                        <span className="text-success ml-2">
                                            Created: {result.positionNumber} (ID: {result.positionId})
                                        </span>
                                    ) : (
                                        <span className="text-critical ml-2">Failed: {result.error}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
